import React from 'react'
import PropTypes from 'prop-types'
import Font from '@cred/react-font'
import Tooltip from 'components/tooltip'
import Link from 'components/link'
import CtaButton from 'components/cta-button'
import { fireGtmEvent } from 'utils/gtm'
import { SnowplowContext } from '@cred/react-analytics/SnowplowContextManager'
import { creditCard as spCreditCard } from '@cred/react-analytics/contexts'
import CreditNeededInfoContent from 'credit-card/card-table/card-tile/CreditNeededInfoContent'
import { ResizedImage } from '@cred/react-resized-image'
import CardDetailsModal from '../../card-details-modal'
import styles from './styles.module.scss'

const credits = {
    'excellent-credit': 'Excellent',
    'good-credit': 'Good',
    'fair-credit': 'Fair',
    'poor-credit': 'Poor',
  },

  getFirstColumnDetails = (category, card) => {
    const { annualFeeFormatted, balanceTransferFeeFormatted } = card

    if (category === 'balance-transfer') {
      return {
        title: 'Balance Transfer Fee',
        value: balanceTransferFeeFormatted,
      }
    }

    return {
      title: 'Annual Fee',
      value: annualFeeFormatted,
    }
  },

  getIntroBonus = (introBonus, introBonusPoints, rewardsUnit) => {
    switch (rewardsUnit) {
      case 'dollars':
        return introBonus ? `$${introBonus}` : 'N/A'
      case 'points':
        return introBonusPoints ? `${introBonusPoints} points` : 'N/A'
      case 'miles':
        return introBonusPoints ? `${introBonusPoints} miles` : 'N/A'
      default:
    }
  },

  getLastColumnDetails = (category, card) => {
    const {
        introBonus,
        introBonusPoints,
        rewardsUnit,
        introBalanceTransferPeriodFormatted,
        introPurchaseAprPeriodFormatted,
        securityDepositMinimum,
        aprRangeFormatted,
      } = card,
      securityDepositMinText = securityDepositMinimum ? `$${securityDepositMinimum.toLocaleString()}` : 'N/A'

    switch (category) {
      case 'rewards':
        return { title: 'Welcome Offer', value: getIntroBonus(introBonus, introBonusPoints, rewardsUnit) }
      case 'balance-transfer':
        return { title: '0% Balance Transfer Period', value: introBalanceTransferPeriodFormatted }
      case 'zero-percent':
        return { title: '0% Purchase Period', value: introPurchaseAprPeriodFormatted }
      case 'secured':
        return { title: 'Security Deposit Min.', value: securityDepositMinText || 'N/A' }
      case 'student':
        return { title: 'Ongoing Variable APR', value: aprRangeFormatted }
      case 'low-interest':
        return { title: '0% Purchase Period', value: introPurchaseAprPeriodFormatted }
      default:
        return ''
    }
  },

  getCreditNeeded = (creditNeeded) => creditNeeded.map(value => credits[value]).join(', '),

  onClickCta = (category, widgetContext) => () => {
    fireGtmEvent('ctaClick', { category, ...widgetContext })
  },

  CardTile = ({ card, category, ctaUrl, widgetContext, isModal, closeModal, idx, uuid }) => {
    const { slug, name, featuredCategory, image, issuerPublicName, creditNeeded, ratesAndFeesUrl } = card,
      cardCategory = (category === 'featured') ? featuredCategory.slug : category,
      firstColumnDetails = getFirstColumnDetails(cardCategory, card),
      lastColumnDetails = getLastColumnDetails(cardCategory, card),
      creditNeededInfo = creditNeeded.includes('new-credit') ? 'None' : getCreditNeeded(creditNeeded),
      modalCls = isModal ? 'modalTile' : '',
      isAmex = ratesAndFeesUrl ? 'isAmex' : '',
      tooltipId = isModal ? `creditNeededInfo-${slug}-${modalCls}` : `creditNeededInfo-${slug}`,
      ctaContext = [
        spCreditCard.analytics({ redirectUUID: uuid, issuer: issuerPublicName, cardName: name, category }),
      ]
    return (
      <div className={`${styles.cardTile} ${styles[modalCls]} ${idx === 0 ? '' : 'mt-8'}`}>
        <div className={`${styles.cardInfoSection} ${styles[modalCls]}`}>
          <div className={styles.imageContainer}>
            <ResizedImage className={styles.cardImg} src={image} alt={`${name} logo`} width={88} height={55} />
            <Font type="bodyLarge" weight="bold" color="neutral" className={`${styles.cardName} ml-8 hidden-xs`}>
              {name}
            </Font>
          </div>
          <div className={styles.ctaSection}>
            <SnowplowContext contexts={ctaContext}>
              <CtaButton blank href={ctaUrl} onClick={onClickCta(category, widgetContext)} small>Apply Now
              </CtaButton>
            </SnowplowContext>
            <Font type="disclaimer" color="lighter" className={`${styles.issuerSite} ${styles[isAmex]} align-center`}>
              {`on ${issuerPublicName}'s Website`}
            </Font>
            {!!ratesAndFeesUrl && (
              <Font type="disclaimer" weight="semiBold" color="secondary" className={`align-center ${styles.ratesFees}`}>
                <Link
                  underline="dashed"
                  blank
                  rel="nofollow"
                  className={styles.ratesFees}
                  href={ratesAndFeesUrl}
                >
                  Rates and fees
                </Link>
              </Font>
            )}
          </div>
        </div>
        <Font type="bodySmall" weight="bold" color="neutral" className={`${styles.cardName} ${styles.mobile} hidden-sm hidden-md hidden-lg hidden-xl`}>
          {name}
        </Font>

        <div className={`${styles.cardDetailsSection} ${styles[modalCls]}`}>
          <div className={styles.detailsContainer}>
            <div className={styles.info}>
              <Font type="disclaimer" color="lighter" className={styles.detailsTitle}>
                { firstColumnDetails.title }
              </Font>
              <Font type="bodySmall" weight="semiBold" className={styles.detailsValue}>
                { firstColumnDetails.value }
              </Font>
            </div>
            <div className={styles.info}>
              <Font type="disclaimer" color="lighter" className={styles.detailsTitle}>
                  Credit Needed
              </Font>
              <Font type="bodySmall" weight="semiBold" className={styles.detailsValue}>
                { creditNeededInfo }
                <Link
                  rel="nofollow"
                  data-tip
                  data-for={tooltipId}
                  className={styles.icon}
                >
                  <i className="cred-icon icon-help-outline" />
                </Link>
              </Font>
              <Tooltip
                id={tooltipId}
                place={isModal ? 'bottom' : 'top'}
                className={styles.tooltip}
                analyticsOptions={{ contentSubject: 'Credit Needed Tooltip' }}
              >
                <CreditNeededInfoContent />
              </Tooltip>
            </div>
            <div className={`${styles.info} hidden-xs`}>
              <Font type="disclaimer" color="lighter" className={styles.detailsTitle}>
                { lastColumnDetails.title }
              </Font>
              <Font type="bodySmall" weight="semiBold" className={styles.detailsValue}>
                { lastColumnDetails.value }
              </Font>
            </div>
          </div>
          { isModal ? (
            <Font className={`${styles.closeDetails} ${styles[modalCls]}`} onClick={closeModal}>Close Card Details</Font>
          ) : (
            <CardDetailsModal
              card={card}
              ctaUrl={ctaUrl}
              category={category}
              widgetContext={widgetContext}
            />
          )}
        </div>
      </div>
    )
  }

CardTile.propTypes = {
  card: PropTypes.object.isRequired,
  category: PropTypes.string.isRequired,
  ctaUrl: PropTypes.string.isRequired,
  widgetContext: PropTypes.object,
  isModal: PropTypes.bool,
  closeModal: PropTypes.func,
  idx: PropTypes.number,
  uuid: PropTypes.string.isRequired,
}

CardTile.defaultProps = {
  isModal: false,
  closeModal: () => {},
}

export { CardTile, getFirstColumnDetails, getIntroBonus, getLastColumnDetails, getCreditNeeded, onClickCta }

export default CardTile
